import apiClient from './client';

/**
 * AI Chat API client
 */

/**
 * Send a message to the AI assistant
 * @param {string} message - User's message
 * @param {Array} conversationHistory - Previous messages for context
 * @param {string} fileContextId - Optional file context ID for conversational import
 * @returns {Promise} API response with AI reply
 */
export const sendChatMessage = async (message, conversationHistory = [], fileContextId = null) => {
  const payload = {
    message,
    conversation_history: conversationHistory
  };
  
  // Phase 5: Include file context ID if available
  if (fileContextId) {
    payload.file_context_id = fileContextId;
  }
  
  const response = await apiClient.post('/api/v1/ai-chat/chat', payload);
  return response.data;
};

/**
 * Get suggested questions for the chat
 * @returns {Promise} API response with suggested questions
 */
export const getSuggestedQuestions = async () => {
  const response = await apiClient.get('/api/v1/ai-chat/suggestions');
  return response.data;
};

/**
 * Check AI chat service health
 * @returns {Promise} Health status
 */
export const checkAIChatHealth = async () => {
  const response = await apiClient.get('/api/v1/ai-chat/health');
  return response.data;
};

/**
 * Upload a file for smart import analysis
 * @param {File} file - The file to upload
 * @returns {Promise} Analysis result with preview of changes
 */
export const uploadFileForImport = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post('/api/v1/ai-chat/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Execute a pending import after user confirmation
 * Phase 4: Now supports reasoning capture for organizational memory
 * @param {string} importId - The import ID from analysis
 * @param {string} [reasoningId] - ID of selected reasoning option
 * @param {string} [reasoningText] - Label of selected reasoning option
 * @param {string} [customReasoning] - Custom text if "other" selected
 * @returns {Promise} Import result
 */
export const executeImport = async (importId, reasoningId = null, reasoningText = null, customReasoning = null) => {
  const response = await apiClient.post('/api/v1/ai-chat/import/execute', {
    import_id: importId,
    reasoning_id: reasoningId,
    reasoning_text: reasoningText,
    custom_reasoning: customReasoning
  });
  return response.data;
};

/**
 * Cancel a pending import
 * @param {string} importId - The import ID to cancel
 * @returns {Promise} Cancellation result
 */
export const cancelImport = async (importId) => {
  const response = await apiClient.delete(`/api/v1/ai-chat/import/${importId}`);
  return response.data;
};

// ============= Conversational Import API (Phase 5) =============

/**
 * Upload a file for conversational import
 * This stores the file context in the database for AI-driven conversation
 * @param {File} file - The file to upload
 * @returns {Promise} Context ID and summary for AI conversation
 */
export const uploadForConversation = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post('/api/v1/ai-chat/upload-for-conversation', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Get current file context for conversational import
 * @param {string} contextId - The context ID
 * @returns {Promise} File context details
 */
export const getFileContext = async (contextId) => {
  const response = await apiClient.get(`/api/v1/ai-chat/context/${contextId}`);
  return response.data;
};

/**
 * Clear/cancel a file context
 * @param {string} contextId - The context ID to clear
 * @returns {Promise} Clear result
 */
export const clearFileContext = async (contextId) => {
  const response = await apiClient.delete(`/api/v1/ai-chat/context/${contextId}`);
  return response.data;
};

/**
 * Get user's active file contexts
 * @param {number} limit - Max number of contexts to return
 * @returns {Promise} List of active contexts
 */
export const getActiveContexts = async (limit = 5) => {
  const response = await apiClient.get(`/api/v1/ai-chat/contexts/active?limit=${limit}`);
  return response.data;
};

// ============= AI Actions API (Phase 7) =============

/**
 * Preview an AI action before executing
 * @param {string} actionType - Type of action (e.g., 'create_opportunity')
 * @param {Object} params - Action parameters
 * @returns {Promise} Preview result with changes and warnings
 */
export const previewAction = async (actionType, params) => {
  const response = await apiClient.post('/api/v1/ai-chat/actions/preview', {
    action_type: actionType,
    params
  });
  return response.data;
};

/**
 * Execute an AI action
 * @param {string} actionType - Type of action
 * @param {Object} params - Action parameters
 * @param {Object} reasoning - Optional reasoning context
 * @returns {Promise} Execution result
 */
export const executeAction = async (actionType, params, reasoning = null) => {
  const response = await apiClient.post('/api/v1/ai-chat/actions/execute', {
    action_type: actionType,
    params,
    reasoning
  });
  return response.data;
};

/**
 * Undo a previously executed action
 * @param {string} actionId - ID of the action to undo
 * @returns {Promise} Undo result
 */
export const undoAction = async (actionId) => {
  const response = await apiClient.post('/api/v1/ai-chat/actions/undo', {
    action_id: actionId
  });
  return response.data;
};

/**
 * Get recent undoable actions
 * @param {number} limit - Max number of actions to return
 * @returns {Promise} List of undoable actions
 */
export const getUndoStack = async (limit = 5) => {
  const response = await apiClient.get(`/api/v1/ai-chat/actions/undo-stack?limit=${limit}`);
  return response.data;
};
